import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Send, Calendar } from "lucide-react";

const AdminSurveyReportTest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const { toast } = useToast();

  const handleSendTestReport = async () => {
    setIsLoading(true);
    try {
      console.log("Triggering test survey report...");
      
      const { data, error } = await supabase.functions.invoke('send-survey-report', {
        body: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate + 'T23:59:59').toISOString(),
          test: true
        }
      });

      if (error) {
        console.error("Error sending test report:", error);
        throw error;
      }

      console.log("Test report sent successfully:", data);

      toast({
        title: "Test Report Sent",
        description: "The survey report has been sent to the admin team.",
      });
    } catch (error) {
      console.error("Failed to send test report:", error);
      toast({
        title: "Error",
        description: "Failed to send test report. Please check the logs.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30 pt-28 pb-12">
      <div className="container mx-auto px-4 max-w-2xl">
        <Card className="shadow-xl">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              <Mail className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-3xl font-bold text-primary">
              Survey Report Test
            </CardTitle>
            <p className="text-lg text-muted-foreground">
              Send a test survey report to the admin team
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">Report Details</span>
              </div>
              <p className="text-sm text-muted-foreground">
                This will generate a survey report for the selected date range and send it to:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• info@seedpotatoorganisation.com</li>
                <li>• support@seedpotatoorganisation.co.uk</li>
              </ul>
            </div>

            <div className="flex justify-center pt-4">
              <Button 
                onClick={handleSendTestReport} 
                disabled={isLoading}
                size="lg"
                className="w-full sm:w-auto"
              >
                <Send className="h-4 w-4 mr-2" />
                {isLoading ? "Sending..." : "Send Test Report"}
              </Button>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>The daily report is automatically scheduled to run at 8:00 AM UK time.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSurveyReportTest;